import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Brain,
  AlertTriangle,
  ExternalLink,
  Layers,
  ScanLine,
  Target,
  Cpu,
  MemoryStick,
  Sparkles,
  Info,
  Copy,
  Check,
} from 'lucide-react';
import {
  mockMONAI15InstanceDetails,
  MONAIBundle,
  MONAITaskType,
} from '@/mock/gpu-instance';
import { MONAIBundleSelector, MONAIBundleCard } from '../../components/monai';

const MONAIModelsPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [selectedBundle, setSelectedBundle] = useState<MONAIBundle | null>(null);
  const [bundleCardOpen, setBundleCardOpen] = useState(false);
  const [bundleCardBundle, setBundleCardBundle] = useState<MONAIBundle | null>(null);
  const [copiedPath, setCopiedPath] = useState<string | null>(null);

  const details = useMemo(
    () => mockMONAI15InstanceDetails(instanceId || ''),
    [instanceId]
  );

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPath(text);
      setTimeout(() => setCopiedPath(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleViewBundleCard = (bundle: MONAIBundle) => {
    setBundleCardBundle(bundle);
    setBundleCardOpen(true);
  };

  const getTaskInfo = (task: MONAITaskType) => {
    switch (task) {
      case 'segmentation':
        return {
          name: 'Segmentation',
          icon: Layers,
          color: 'text-purple-500',
          bgColor: 'bg-purple-500/10',
        };
      case 'classification':
        return {
          name: 'Classification',
          icon: ScanLine,
          color: 'text-blue-500',
          bgColor: 'bg-blue-500/10',
        };
      case 'detection':
        return {
          name: 'Detection',
          icon: Target,
          color: 'text-green-500',
          bgColor: 'bg-green-500/10',
        };
      case 'registration':
        return {
          name: 'Registration',
          icon: Brain,
          color: 'text-orange-500',
          bgColor: 'bg-orange-500/10',
        };
    }
  };

  // Statistics
  const segmentationBundles = details.bundles.filter(
    (b) => b.task === 'segmentation'
  ).length;
  const classificationBundles = details.bundles.filter(
    (b) => b.task === 'classification'
  ).length;
  const detectionBundles = details.bundles.filter(
    (b) => b.task === 'detection'
  ).length;

  return (
    <div className="container py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6 text-red-500" />
            MONAI Model Zoo
          </h1>
          <p className="text-muted-foreground">
            Pre-trained MONAI Bundles from NGC Catalog
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() =>
            window.open(
              'https://monai.io/model-zoo.html',
              '_blank'
            )
          }
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Open MONAI Model Zoo
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{details.bundles.length}</div>
            <div className="text-sm text-muted-foreground">Total Bundles</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {segmentationBundles}
            </div>
            <div className="text-sm text-muted-foreground">Segmentation</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {classificationBundles}
            </div>
            <div className="text-sm text-muted-foreground">Classification</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {detectionBundles}
            </div>
            <div className="text-sm text-muted-foreground">Detection</div>
          </CardContent>
        </Card>
      </div>

      {/* Bundle Browser */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Available Bundles</CardTitle>
          <CardDescription>
            Click on a bundle to view details and select for training
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MONAIBundleSelector
            bundles={details.bundles}
            selectedBundleId={selectedBundle?.id}
            onSelectBundle={setSelectedBundle}
            onViewBundleCard={handleViewBundleCard}
          />
        </CardContent>
      </Card>

      {/* Selected Bundle Details */}
      {selectedBundle && (
        <Card className="border-primary/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getTaskInfo(selectedBundle.task).bgColor}`}>
                  {(() => {
                    const Icon = getTaskInfo(selectedBundle.task).icon;
                    return (
                      <Icon
                        className={`h-5 w-5 ${getTaskInfo(selectedBundle.task).color}`}
                      />
                    );
                  })()}
                </div>
                <div>
                  <CardTitle>{selectedBundle.displayName}</CardTitle>
                  <CardDescription>{selectedBundle.description}</CardDescription>
                </div>
              </div>
              <Badge variant="secondary" className="font-mono">
                v{selectedBundle.version}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Performance Metrics */}
            <div className="grid grid-cols-4 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground mb-1">
                  <Sparkles className="h-4 w-4" />
                  {selectedBundle.diceScore
                    ? 'Dice Score'
                    : selectedBundle.accuracy
                    ? 'Accuracy'
                    : 'AUC'}
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {(
                    (selectedBundle.diceScore ||
                      selectedBundle.accuracy ||
                      selectedBundle.auc ||
                      0) * 100
                  ).toFixed(1)}
                  %
                </div>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground mb-1">
                  <MemoryStick className="h-4 w-4" />
                  VRAM Required
                </div>
                <div className="text-2xl font-bold">
                  {selectedBundle.vramRequired} GB
                </div>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground mb-1">
                  <Cpu className="h-4 w-4" />
                  Architecture
                </div>
                <div className="text-xl font-bold">{selectedBundle.architecture}</div>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <div className="text-sm text-muted-foreground mb-1">Input Size</div>
                <div className="text-xl font-bold font-mono">
                  {selectedBundle.inputSize}
                </div>
              </div>
            </div>

            {/* Bundle Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Task Type</div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const info = getTaskInfo(selectedBundle.task);
                      const Icon = info.icon;
                      return (
                        <>
                          <Icon className={`h-4 w-4 ${info.color}`} />
                          <span className="capitalize">{info.name}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Modality</div>
                  <span>{selectedBundle.modality}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">NGC Path</div>
                  <div className="flex items-center justify-between">
                    <code className="text-sm font-mono truncate">
                      {selectedBundle.ngcPath}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 ml-2"
                      onClick={() => handleCopy(selectedBundle.ngcPath)}
                    >
                      {copiedPath === selectedBundle.ngcPath ? (
                        <Check className="h-3 w-3" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">
                    Bundle Version
                  </div>
                  <span className="font-mono">v{selectedBundle.version}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                className="flex-1"
                variant="outline"
                onClick={() => handleViewBundleCard(selectedBundle)}
              >
                <Info className="h-4 w-4 mr-2" />
                View Full Details
              </Button>
              <Button
                className="flex-1"
                variant="outline"
                onClick={() =>
                  window.open(
                    `https://catalog.ngc.nvidia.com/orgs/nvidia/teams/monailabel/models/${selectedBundle.name}`,
                    '_blank'
                  )
                }
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View on NGC Catalog
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bundle Card Dialog */}
      <MONAIBundleCard
        bundle={bundleCardBundle}
        open={bundleCardOpen}
        onOpenChange={setBundleCardOpen}
        onSelectBundle={(bundle) => {
          setSelectedBundle(bundle);
          setBundleCardOpen(false);
        }}
      />

      {/* Demo Warning */}
      <Alert className="border-yellow-500/50 bg-yellow-500/5">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <span className="font-medium">Demo Mode:</span> These bundles are from the
          MONAI Model Zoo. In production, bundles will be downloaded automatically
          when you create an experiment.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default MONAIModelsPage;
